/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365All from '@msdyn365-commerce/core';
import Msdyn365 from '@msdyn365-commerce/core';
import fetchChooseYourFoundationDatabaseDataAction, { FetchChooseYourFoundationDatabaseInput } from '../../actions/fetch-choose-your-foundation-database.action';
import { getQueryParamsFromQueryByName, getSelectedProductIdFromActionInput } from '../../data-actions/utilities/utils';
import { getDefaultVariantId } from '../../data-actions/get-selected-variant.override.action';
import { variantQueryStringName } from '../../Utilities/constants';

/**
 * GetUnbxdCollectionCompareData Input Action
 */
export class GetUnbxdCollectionCompareDataInput implements Msdyn365All.IActionInput {
    //   Construct the input needed to run the action
    public configs?: any;
    public recordId?: string;
    constructor(configs?: any, recordId?: string) {
        this.configs = configs;
        this.recordId = recordId;
    }

    //   Determine if the results of this get action should cache the results and if so provide
    // a cache object type and an appropriate cache key
    public getCacheKey = () => `VariantIDs-${this.recordId}`;
    public getCacheObjectType = () => 'VariantIDs';
    public dataCacheType = (): Msdyn365All.CacheType => 'request';
}

//   Create a data model here or import one to capture the response of the action
export interface IGetUnbxdCollectionCompareDataData {
    collectionData: any | null;
    productId: number | undefined | null;
    variantIds: string[] | undefined;
    collectionDescription: string | undefined;
}

/**
 *   Use this function to create the input required to make the action call
 */
const createInput = (args: Msdyn365All.ICreateActionContext): Msdyn365All.IActionInput => {
    const variantId = args.requestContext.app.config.queryBasedVariantSelection
        ? getQueryParamsFromQueryByName(
            Msdyn365.isBrowser
                ? new URL(window.location.href.toString())
                : args.requestContext.url.requestUrl,
            variantQueryStringName)
        : undefined;
    return new GetUnbxdCollectionCompareDataInput(args, variantId);
};

/**
 *   Use this function to call your action and process the results as needed
 */
async function getUnbxdCollectionCompare(input: GetUnbxdCollectionCompareDataInput, ctx: Msdyn365All.IActionContext): Promise<IGetUnbxdCollectionCompareDataData> {
    let variantIdNumber: number | null | undefined;
    const actionInput = new FetchChooseYourFoundationDatabaseInput(ctx.requestContext.app.config.jsonSrc);
    const unbxdApiKey = ctx.requestContext.app.config.unbxdApiKey;
    const unbxdSiteKey = ctx.requestContext.app.config.unbxdSiteKey;
    const collectionJson: any = await fetchChooseYourFoundationDatabaseDataAction(actionInput, ctx);
    let collectionData: any | null = {};
    let variantIds: string[] | undefined = [];
    let collectionDescription: string | undefined;
    let response: any = {};
    if (collectionJson) {
        const baseURL = 'https://search.unbxd.io';
        variantIdNumber = input.recordId && !Number.isNaN(Number(input.recordId))
            ? Number(input.recordId)
            : await getDefaultVariantId(Number(getSelectedProductIdFromActionInput(ctx)), ctx);
        if (collectionJson.length > 0 && variantIdNumber) {
            variantIds = collectionJson?.find((item: any) => Number(item.mattressVariantReqId) === variantIdNumber)?.mattressComparisonReqIdList?.map((value: any) => value);
            collectionDescription = collectionJson?.find((item: any) => Number(item.mattressVariantReqId) === variantIdNumber)?.collectionDescription;
            variantIds && variantIds?.length >= 1 && variantIds?.unshift(`${variantIdNumber}`);
            let collectionCompareIds = '';
            if (variantIds && variantIds?.length >= 2) {
                for (let index = 0; index < variantIds?.length; index++) {
                    collectionCompareIds += index === 0 ? `variantId:${variantIds[index]}` : ` OR variantId:${variantIds[index]}`;
                }
            } else {
                return { collectionData: null, productId: null, variantIds: undefined, collectionDescription };
            }
            if (collectionCompareIds.length) {
                const unbxdUrl = `${baseURL}/${unbxdApiKey}/${unbxdSiteKey}/search?q=*&variants=true&variants.count=*&filter= ${collectionCompareIds}`;
                response = await Msdyn365All.sendRequest<any>(unbxdUrl, 'get');
            } else {
                return { collectionData: null, productId: null, variantIds: undefined, collectionDescription };
            }

            collectionData = response?.data?.response;
            return { collectionData, productId: variantIdNumber, variantIds: variantIds, collectionDescription };
        }
    }
    //   Uncomment the below line to get the value from a service
    // const response = await Msdyn365.sendRequest<IGetUnbxdCollectionCompareDataData[]>('/get/example/id/1', 'get');
    return { collectionData: null, productId: null, variantIds: undefined, collectionDescription };
}

export default Msdyn365All.createObservableDataAction({
    action: <Msdyn365All.IAction<IGetUnbxdCollectionCompareDataData>>getUnbxdCollectionCompare,
    //   Give your data action a unique id
    id: 'GetUnbxdCollectionCompareData',
    input: createInput
    //   Uncomment the below line if this is a meant to be a batched data action
    // isBatched: true
});


